import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../utils/supabase';
import { companyCall, getPeople, parseBulkInvites, SEATS_FULL, seatsUsed } from '../../utils/company';
import { UpgradeNote } from '../Upgrade';
import { errorBox, inputClass, noticeBox, secondaryButton } from '../AuthLayout';
import { CardStatus, RoleBadge, formatDate } from './bits';
import BillingButton from '../BillingButton';
import { hasPaidAccess, hasStripeBilling } from '../../utils/billing';

const ROLE_LABELS = { owner: 'Owner', admin: 'Admin', member: 'Member' };

/** Company → People: members, invites, cards, seats, invite (single + bulk). */
export default function People({ org, myRole, isPlatformAdmin, userId }) {
  const [people, setPeople] = useState(null);
  const [error, setError] = useState('');
  const [panel, setPanel] = useState(null); // 'invite' | 'bulk' | null
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(null); // key of the row action in flight
  const [confirming, setConfirming] = useState(null);
  const [rowError, setRowError] = useState({});
  const [signinLink, setSigninLink] = useState(null); // { email, link, copied } — platform admin only

  const load = useCallback(async () => {
    try {
      setPeople(await getPeople(org.id));
    } catch (e) {
      setError(e.message || 'Could not load the people in this company.');
    }
  }, [org.id]);
  useEffect(() => { load(); }, [load]);

  const members = useMemo(() => (people || []).filter((p) => p.kind === 'member')
    .sort((a, b) => roleRank(a.role) - roleRank(b.role) || (a.full_name || a.email).localeCompare(b.full_name || b.email)), [people]);
  const invites = useMemo(() => (people || []).filter((p) => p.kind === 'invite'), [people]);
  const loose = useMemo(() => (people || []).filter((p) => p.kind === 'card'), [people]);
  const used = people ? seatsUsed(people) : 0;
  const limit = org.seat_limit;
  const remaining = limit == null ? Infinity : Math.max(0, limit - used);
  // Adding the team is a Teams feature (platform admins can still set a free company up)
  const canInvite = isPlatformAdmin || hasPaidAccess(org);
  const full = remaining <= 0 && !isPlatformAdmin;
  const actsAsOwner = isPlatformAdmin || myRole === 'owner';
  const billed = hasStripeBilling(org);
  const canBill = isPlatformAdmin || myRole === 'owner' || myRole === 'admin';
  const owners = members.filter((m) => m.role === 'owner' && m.status === 'active').length;
  const takenEmails = [...members.map((m) => m.email), ...invites.filter((i) => i.status === 'pending').map((i) => i.email)];

  /** Run a company-members action for one row, then reload. */
  const act = async (key, action, payload) => {
    setBusy(key);
    setRowError((r) => ({ ...r, [key]: '' }));
    try {
      const data = await companyCall(action, org.id, payload);
      if (action === 'resend_invite') setResult({ ...data, resent: true });
      await load();
    } catch (e) {
      setRowError((r) => ({ ...r, [key]: e.message }));
    } finally {
      setBusy(null);
      setConfirming(null);
    }
  };

  /** Platform admin: a one-time sign-in link for an invitee whose email never arrived. */
  const makeSigninLink = async (key, inviteId) => {
    setBusy(key);
    setRowError((r) => ({ ...r, [key]: '' }));
    setSigninLink(null);
    try {
      const { data, error: fnError } = await supabase.functions.invoke('admin-users', { body: { action: 'signin_link', invite_id: inviteId } });
      if (fnError) {
        const body = await fnError.context?.json?.().catch(() => null);
        throw new Error(body?.error || fnError.message);
      }
      let copied = false;
      try { await navigator.clipboard.writeText(data.link); copied = true; } catch { /* clipboard blocked */ }
      setSigninLink({ email: data.email, link: data.link, copied });
    } catch (e) {
      setRowError((r) => ({ ...r, [key]: e.message }));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-6">
      {error && <div className={errorBox}>{error}</div>}

      <div className="flex flex-wrap items-center gap-4">
        <SeatMeter used={used} limit={limit} loading={!people} />
        <div className="flex flex-wrap gap-2.5 ml-auto">
          {billed && canBill && <BillingButton orgId={org.id} label="Change seats & billing" />}
          {canInvite && (
            <>
              <button onClick={() => { setPanel(panel === 'bulk' ? null : 'bulk'); setResult(null); }} className={secondaryButton} disabled={!people || full}>
                Bulk invite
              </button>
              <button onClick={() => { setPanel(panel === 'invite' ? null : 'invite'); setResult(null); }} disabled={!people || full}
                className="inline-flex items-center h-11 px-5 rounded-xl bg-blue text-white font-semibold hover:bg-blue-dark disabled:opacity-50">
                Invite person
              </button>
            </>
          )}
        </div>
      </div>

      {!canInvite && (
        <UpgradeNote org={org} canManage={canBill}>
          Bring your team to Carduzo: with Teams, everyone gets a card with your company's logo and colors, and you see every lead in one place.
        </UpgradeNote>
      )}
      {full && canInvite && org.billing_status === 'complimentary' && org.offer === 'team25' && (
        <UpgradeNote org={org} canManage={canBill}>
          Your card is free with the founder offer. To give the rest of your team a card, switch to Teams: 25% off for the first year.
        </UpgradeNote>
      )}
      {full && canInvite && !(org.billing_status === 'complimentary' && org.offer === 'team25') && <div className={errorBox}>{billed && canBill ? "You've used all your seats. Add more with “Change seats & billing”." : SEATS_FULL}</div>}
      {result && <InviteResult result={result} onClose={() => setResult(null)} />}

      {panel === 'invite' && (
        <InviteForm org={org} full={full} onCancel={() => setPanel(null)}
          onDone={async (res) => { setResult(res); setPanel(null); await load(); }} />
      )}
      {panel === 'bulk' && (
        <BulkInvite org={org} remaining={isPlatformAdmin ? Infinity : remaining} takenEmails={takenEmails}
          onClose={() => setPanel(null)} onSent={load} />
      )}

      <Group title="Members" count={members.length}>
        {people === null ? <Empty>Loading people...</Empty> : members.length === 0 ? (
          <Empty>Nobody has joined yet. People show up here once they accept their invitation.</Empty>
        ) : (
          <Rows
            head={['Person', 'Role', 'Joined', 'Card', 'Leads', '']}
            items={members.map((m) => {
              const self = m.user_id === userId;
              const ownerLocked = m.role === 'owner' && !actsAsOwner;
              const lastOwner = m.role === 'owner' && owners <= 1;
              const key = `m-${m.user_id}`;
              return {
                key,
                person: <Person name={m.full_name} email={m.email} you={self} />,
                role: self || ownerLocked ? <RoleBadge role={m.role} /> : (
                  <select
                    value={m.role}
                    disabled={busy === key}
                    onChange={(e) => act(key, 'change_role', { user_id: m.user_id, role: e.target.value })}
                    aria-label={`Role for ${m.full_name || m.email}`}
                    className="h-9 pl-2.5 pr-8 border border-line-strong rounded-lg text-sm text-ink bg-white focus:outline-none focus:ring-2 focus:ring-blue"
                  >
                    {['member', 'admin', ...(actsAsOwner ? ['owner'] : [])].map((r) => (
                      <option key={r} value={r} disabled={lastOwner && r !== 'owner'}>{ROLE_LABELS[r]}</option>
                    ))}
                  </select>
                ),
                joined: formatDate(m.joined_at),
                card: <CardCell row={m} orgId={org.id} />,
                leads: m.card_id ? (m.lead_count ?? 0) : '—',
                actions: (
                  <RowActions>
                    {m.card_id && (
                      <CardToggle row={m} busy={busy === `c-${m.card_id}`}
                        onToggle={() => act(`c-${m.card_id}`, 'set_card_status', { card_id: m.card_id, status: m.card_status === 'active' ? 'suspended' : 'active' })} />
                    )}
                    {!self && !ownerLocked && !lastOwner && (
                      <Confirm id={key} confirming={confirming} setConfirming={setConfirming} busy={busy === key}
                        label="Remove" confirmLabel="Remove" note="Their card will be suspended."
                        onConfirm={() => act(key, 'remove_member', { user_id: m.user_id })} />
                    )}
                  </RowActions>
                ),
                error: rowError[key] || rowError[`c-${m.card_id}`],
              };
            })}
          />
        )}
      </Group>

      <Group title="Pending invites" count={invites.length}>
        {signinLink && (
          <div className={`${noticeBox} mb-3 text-sm`}>
            <p className="font-semibold">Sign-in link for {signinLink.email}{signinLink.copied ? ' (copied)' : ''}</p>
            <p className="mt-1">Send it to them yourself (a text or Teams message works best). It works once and expires soon; they press Continue, choose a password and join.</p>
            <code className="block mt-2 bg-white/70 px-2 py-1 rounded break-all text-xs">{signinLink.link}</code>
            <div className="mt-2 flex gap-2">
              <button onClick={async () => { try { await navigator.clipboard.writeText(signinLink.link); setSigninLink({ ...signinLink, copied: true }); } catch { /* blocked */ } }}
                className="h-8 px-3 rounded-lg border border-line-strong bg-white text-xs font-semibold">{signinLink.copied ? 'Copied' : 'Copy link'}</button>
              <button onClick={() => setSigninLink(null)} className="h-8 px-3 text-xs font-semibold underline">Done</button>
            </div>
          </div>
        )}
        {people !== null && invites.length === 0 ? <Empty>No open invites.</Empty> : people !== null && (
          <Rows
            head={['Invited', 'Role', 'Sent', 'Expires', 'Card', '']}
            items={invites.map((inv) => {
              const key = `i-${inv.invite_id}`;
              const expired = inv.status === 'expired';
              return {
                key,
                person: <Person name={inv.full_name} email={inv.email} />,
                role: <RoleBadge role={inv.role} />,
                joined: formatDate(inv.invited_at),
                expires: expired
                  ? <span className="inline-flex h-6 px-2 rounded-full bg-coral-soft text-coral-ink text-xs font-semibold items-center">Expired</span>
                  : formatDate(inv.expires_at),
                card: <CardCell row={inv} orgId={org.id} />,
                actions: (
                  <RowActions>
                    <button onClick={() => act(key, 'resend_invite', { invite_id: inv.invite_id })} disabled={!!busy}
                      className="h-8 px-2 text-xs font-semibold text-blue hover:underline disabled:opacity-50">
                      {busy === key ? 'Sending...' : 'Resend'}
                    </button>
                    {isPlatformAdmin && !expired && (
                      <button onClick={() => makeSigninLink(key, inv.invite_id)} disabled={!!busy}
                        title="One-time link that signs them in without the email. Only for people who have never signed in."
                        className="h-8 px-2 text-xs font-semibold text-blue hover:underline disabled:opacity-50">
                        Copy sign-in link
                      </button>
                    )}
                    <Confirm id={key} confirming={confirming} setConfirming={setConfirming} busy={busy === key}
                      label="Cancel" confirmLabel="Cancel invite" keepLabel="Keep"
                      note={inv.card_id ? 'Their card will be suspended.' : ''}
                      onConfirm={() => act(key, 'cancel_invite', { invite_id: inv.invite_id })} />
                  </RowActions>
                ),
                error: rowError[key],
              };
            })}
            expiresColumn
          />
        )}
      </Group>

      {loose.length > 0 && (
        <Group title="Cards without a person" count={loose.length}
          hint="Cards whose person left the company or never joined. Reactivate one, or invite someone with the same email to give it back.">
          <Rows
            head={['Card', '', '', 'Status', 'Leads', '']}
            items={loose.map((c) => ({
              key: `l-${c.card_id}`,
              person: <Person name={c.card_name} email={c.email} />,
              role: null,
              joined: null,
              card: <CardCell row={c} orgId={org.id} />,
              leads: c.lead_count ?? 0,
              actions: (
                <RowActions>
                  <CardToggle row={c} busy={busy === `c-${c.card_id}`}
                    onToggle={() => act(`c-${c.card_id}`, 'set_card_status', { card_id: c.card_id, status: c.card_status === 'active' ? 'suspended' : 'active' })} />
                </RowActions>
              ),
              error: rowError[`c-${c.card_id}`],
            }))}
            looseCards
          />
        </Group>
      )}
    </div>
  );
}

const roleRank = (r) => ({ owner: 0, admin: 1, member: 2 }[r] ?? 3);

function SeatMeter({ used, limit, loading }) {
  if (loading) return <p className="text-sm text-ink-muted">Counting seats...</p>;
  const pct = limit ? Math.min(100, Math.round((used / limit) * 100)) : 0;
  const full = limit != null && used >= limit;
  return (
    <div className="min-w-[220px]">
      <p className="text-[15px] text-ink">
        <span className="font-display font-bold text-[22px]">{used}</span>
        {limit != null ? <> of <span className="font-semibold">{limit}</span> seats used</> : <> {used === 1 ? 'seat' : 'seats'} used · no seat limit</>}
      </p>
      {limit != null && (
        <div className="h-2 w-full max-w-[260px] rounded-full bg-line mt-1.5 overflow-hidden" role="progressbar" aria-valuemin={0} aria-valuemax={limit} aria-valuenow={used} aria-label="Seats used">
          <div className={`h-full rounded-full ${full ? 'bg-coral-ink' : 'bg-blue'}`} style={{ width: `${pct}%` }} />
        </div>
      )}
      <p className="text-xs text-ink-muted mt-1">Members plus pending invites.</p>
    </div>
  );
}

function Group({ title, count, hint, children }) {
  return (
    <section className="space-y-3">
      <div>
        <h2 className="text-xl font-bold text-ink">{title} <span className="text-ink-muted font-semibold text-base">{count}</span></h2>
        {hint && <p className="text-sm text-ink-muted mt-0.5">{hint}</p>}
      </div>
      {children}
    </section>
  );
}

function Empty({ children }) {
  return <p className="bg-white rounded-2xl border border-dashed border-line-strong px-5 py-6 text-center text-ink-muted text-sm">{children}</p>;
}

function Person({ name, email, you }) {
  return (
    <div className="min-w-0 flex-1">
      <div className="font-semibold text-ink truncate">{name || email}{you && <span className="ml-2 text-xs font-semibold text-blue">You</span>}</div>
      {name && <div className="text-ink-muted text-sm truncate">{email}</div>}
    </div>
  );
}

function CardCell({ row, orgId }) {
  if (!row.card_id) return <span className="text-ink-muted text-sm">No card</span>;
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
      <CardStatus status={row.card_status} />
      {row.card_status === 'active' && (
        <a href={`/c/${row.card_slug}`} target="_blank" rel="noopener noreferrer" className="text-sm font-medium text-blue hover:underline">View</a>
      )}
      <Link to={`/cards/${row.card_id}/edit`} state={{ from: `/company/${orgId}` }} className="text-sm font-medium text-blue hover:underline">Edit</Link>
      <Link to={`/cards/${row.card_id}/print`} state={{ from: `/company/${orgId}` }} className="text-sm font-medium text-ink-muted hover:text-ink hover:underline">NFC design</Link>
    </div>
  );
}

function CardToggle({ row, busy, onToggle }) {
  const live = row.card_status === 'active';
  return (
    <button onClick={onToggle} disabled={busy} className="h-8 px-2 text-xs font-semibold text-ink-muted hover:text-ink disabled:opacity-50 whitespace-nowrap">
      {busy ? 'Saving...' : live ? 'Suspend card' : 'Reactivate card'}
    </button>
  );
}

function RowActions({ children }) {
  return <div className="flex flex-wrap items-center justify-end gap-1">{children}</div>;
}

/** Inline two-step confirm (no browser dialogs). */
function Confirm({ id, confirming, setConfirming, busy, label, confirmLabel, keepLabel = 'Keep', note, onConfirm }) {
  if (confirming === id) {
    return (
      <span className="inline-flex flex-wrap items-center justify-end gap-2">
        {note && <span className="text-xs text-ink-muted">{note}</span>}
        <button onClick={onConfirm} disabled={busy} className="h-8 px-3 rounded-lg bg-coral-ink text-white text-xs font-semibold disabled:opacity-50">
          {busy ? 'Working...' : confirmLabel}
        </button>
        <button onClick={() => setConfirming(null)} className="h-8 px-2 text-xs font-medium text-ink-muted hover:text-ink">{keepLabel}</button>
      </span>
    );
  }
  return (
    <button onClick={() => setConfirming(id)} className="h-8 px-2 text-xs font-semibold text-ink-muted hover:text-coral-ink">{label}</button>
  );
}

/** Table on large screens, stacked cards below lg. */
function Rows({ head, items, expiresColumn = false, looseCards = false }) {
  const cells = (it) => looseCards
    ? [it.person, null, null, it.card, it.leads, it.actions]
    : [it.person, it.role, it.joined, expiresColumn ? it.expires : it.card, expiresColumn ? it.card : it.leads, it.actions];
  return (
    <>
      <ul className="grid grid-cols-1 gap-3 lg:hidden">
        {items.map((it) => (
          <li key={it.key} className="bg-white rounded-2xl border border-line p-4 space-y-3 min-w-0">
            <div className="flex items-start justify-between gap-3">
              {it.person}
              {it.role && <div className="shrink-0">{it.role}</div>}
            </div>
            <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
              {head.slice(1, 5).map((h, i) => {
                const v = cells(it)[i + 1];
                if (!h || v == null || i === 0) return null;
                return (
                  <div key={h} className={h === 'Card' || h === 'Status' ? 'col-span-2 order-last' : ''}>
                    <dt className="text-xs font-semibold tracking-wider uppercase text-ink-muted">{h}</dt>
                    <dd className="mt-0.5 text-ink">{v}</dd>
                  </div>
                );
              })}
            </dl>
            <div className="pt-1 border-t border-line">{it.actions}</div>
            {it.error && <p className="text-sm text-coral-ink">{it.error}</p>}
          </li>
        ))}
      </ul>
      <div className="hidden lg:block bg-white rounded-2xl border border-line overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-surface text-left">
            <tr className="text-xs font-semibold tracking-wider uppercase text-ink-muted">
              {head.map((h, i) => <th key={i} className={`px-4 py-3 ${h === 'Leads' ? 'text-right' : ''}`}>{h || <span className="sr-only">{i === head.length - 1 ? 'Actions' : ''}</span>}</th>)}
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {items.map((it) => (
              <tr key={it.key} className="align-middle">
                {cells(it).map((c, i) => (
                  <td key={i} className={`px-4 py-3 ${i === 0 ? 'max-w-[280px]' : ''} ${head[i] === 'Leads' ? 'text-right tabular-nums' : ''} ${i === head.length - 1 ? 'text-right' : ''} ${head[i] === 'Joined' || head[i] === 'Sent' ? 'whitespace-nowrap text-ink-muted' : ''}`}>
                    {c}
                    {i === head.length - 1 && it.error && <p className="text-xs text-coral-ink mt-1 max-w-[260px] ml-auto text-right">{it.error}</p>}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function Panel({ title, children, onClose }) {
  return (
    <section className="bg-white rounded-2xl border border-line p-5 sm:p-6">
      <div className="flex items-start justify-between gap-4 mb-4">
        <h2 className="text-xl font-bold text-ink">{title}</h2>
        {onClose && <button onClick={onClose} className="text-sm font-medium text-ink-muted hover:text-ink">Close</button>}
      </div>
      {children}
    </section>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-sm font-semibold text-ink mb-1.5">{label}</span>
      {children}
    </label>
  );
}

function InviteForm({ org, full, onDone, onCancel }) {
  const [form, setForm] = useState({ full_name: '', email: '', role: 'member', create_card: true });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      onDone(await companyCall('invite', org.id, form));
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Panel title={`Invite someone to ${org.name}`} onClose={onCancel}>
      <form onSubmit={submit} className="grid sm:grid-cols-2 lg:grid-cols-[1fr_1fr_200px] gap-4">
        <Field label="Name"><input className={inputClass} value={form.full_name} onChange={set('full_name')} disabled={saving || full} placeholder="e.g. Ana García" /></Field>
        <Field label="Email"><input type="email" className={inputClass} value={form.email} onChange={set('email')} required disabled={saving || full} placeholder="ana@company.com" /></Field>
        <Field label="Role">
          <select className={inputClass} value={form.role} onChange={set('role')} disabled={saving || full}>
            <option value="member">Member</option>
            <option value="admin">Admin</option>
          </select>
        </Field>
        <label className="sm:col-span-2 lg:col-span-3 flex items-center gap-2.5 text-sm text-ink">
          <input type="checkbox" checked={form.create_card} onChange={set('create_card')} disabled={saving || full} className="w-4 h-4 accent-[#265EE6]" />
          Create a card for them now (they can finish it after they join)
        </label>
        {error && <div className={`${errorBox} sm:col-span-2 lg:col-span-3`}>{error}</div>}
        <div className="sm:col-span-2 lg:col-span-3 flex flex-wrap gap-3">
          <button type="submit" disabled={saving || full} className="inline-flex items-center h-11 px-5 rounded-xl bg-blue text-white font-semibold hover:bg-blue-dark disabled:opacity-50">
            {saving ? 'Sending...' : 'Send invitation'}
          </button>
          <button type="button" onClick={onCancel} className={secondaryButton}>Cancel</button>
        </div>
      </form>
    </Panel>
  );
}

const STATUS_STYLE = {
  ready: 'text-ink-muted',
  sending: 'text-blue',
  sent: 'text-blue font-semibold',
  failed: 'text-coral-ink',
  invalid: 'text-coral-ink',
};

function BulkInvite({ org, remaining, takenEmails, onClose, onSent }) {
  const [text, setText] = useState('');
  const [rows, setRows] = useState(null);
  const [role, setRole] = useState('member');
  const [createCards, setCreateCards] = useState(true);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const preview = () => {
    let free = remaining;
    setDone(false);
    setRows(parseBulkInvites(text, takenEmails).map((r) => {
      if (r.error) return { ...r, status: 'invalid', message: r.error };
      if (free <= 0) return { ...r, status: 'invalid', message: 'No seat left for this person' };
      free -= 1;
      return { ...r, status: 'ready', message: '' };
    }));
  };

  const ready = (rows || []).filter((r) => r.status === 'ready');
  const send = async () => {
    setSending(true);
    for (const r of ready) {
      setRows((all) => all.map((x) => (x.line === r.line ? { ...x, status: 'sending', message: 'Sending...' } : x)));
      let update;
      try {
        const res = await companyCall('invite', org.id, { email: r.email, full_name: r.full_name, role, create_card: createCards });
        update = { status: 'sent', message: res.emailed ? 'Invitation sent' : (res.email_note || 'Invite created — send them the link'), link: res.emailed ? null : res.invite_link };
      } catch (e) {
        update = { status: 'failed', message: e.message };
      }
      setRows((all) => all.map((x) => (x.line === r.line ? { ...x, ...update } : x)));
    }
    setSending(false);
    setDone(true);
    onSent();
  };

  const counts = rows && {
    ok: rows.filter((r) => r.status === 'ready').length,
    bad: rows.filter((r) => r.status === 'invalid').length,
    sent: rows.filter((r) => r.status === 'sent').length,
    failed: rows.filter((r) => r.status === 'failed').length,
  };

  return (
    <Panel title="Bulk invite" onClose={sending ? null : onClose}>
      {!rows ? (
        <div className="space-y-4">
          <p className="text-sm text-ink-muted">
            Paste one person per line: <span className="font-mono text-ink">Name, email</span> or just an email. You can paste two columns straight from a spreadsheet.
          </p>
          <textarea value={text} onChange={(e) => setText(e.target.value)} rows={7} aria-label="People to invite"
            placeholder={'Ana García, ana@company.com\nLuis Pérez, luis@company.com\nmaria@company.com'}
            className={`${inputClass} h-auto py-2.5 font-mono text-sm leading-6`} />
          <div className="flex flex-wrap gap-3">
            <button onClick={preview} disabled={!text.trim()} className="inline-flex items-center h-11 px-5 rounded-xl bg-blue text-white font-semibold hover:bg-blue-dark disabled:opacity-50">
              Preview list
            </button>
            <button onClick={onClose} className={secondaryButton}>Cancel</button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <p className="text-sm text-ink">
            {done
              ? <><strong>{counts.sent}</strong> invited{counts.failed ? <>, <strong className="text-coral-ink">{counts.failed}</strong> failed</> : ''}{counts.bad ? `, ${counts.bad} skipped` : ''}.</>
              : <><strong>{counts.ok}</strong> ready to invite{counts.bad ? <>, <strong className="text-coral-ink">{counts.bad}</strong> with problems (they will be skipped)</> : ''}.</>}
          </p>
          <div className="border border-line rounded-xl overflow-hidden">
            <ul className="divide-y divide-line max-h-[360px] overflow-y-auto">
              {rows.map((r) => (
                <li key={r.line} className={`px-4 py-2.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm ${r.status === 'invalid' || r.status === 'failed' ? 'bg-coral-soft/50' : ''}`}>
                  <span className="w-8 text-xs text-ink-muted tabular-nums">#{r.line}</span>
                  <span className="flex-1 min-w-[180px]">
                    <span className="font-semibold text-ink">{r.full_name || <span className="text-ink-muted font-normal">No name</span>}</span>
                    <span className="text-ink-muted"> · {r.email || '—'}</span>
                  </span>
                  <span className={`text-sm ${STATUS_STYLE[r.status]}`}>
                    {r.status === 'ready' ? 'Ready' : r.message}
                    {r.link && <button onClick={() => navigator.clipboard?.writeText(r.link)} className="ml-2 underline">Copy link</button>}
                  </span>
                </li>
              ))}
            </ul>
          </div>
          {!done && (
            <div className="flex flex-wrap items-end gap-4">
              <label className="block">
                <span className="block text-sm font-semibold text-ink mb-1.5">Role for everyone</span>
                <select className={`${inputClass} w-44`} value={role} onChange={(e) => setRole(e.target.value)} disabled={sending}>
                  <option value="member">Member</option>
                  <option value="admin">Admin</option>
                </select>
              </label>
              <label className="flex items-center gap-2.5 text-sm text-ink h-11">
                <input type="checkbox" checked={createCards} onChange={(e) => setCreateCards(e.target.checked)} disabled={sending} className="w-4 h-4 accent-[#265EE6]" />
                Create a card for each person
              </label>
            </div>
          )}
          <div className="flex flex-wrap gap-3">
            {!done && (
              <button onClick={send} disabled={sending || !ready.length} className="inline-flex items-center h-11 px-5 rounded-xl bg-blue text-white font-semibold hover:bg-blue-dark disabled:opacity-50">
                {sending ? 'Sending invites...' : `Send ${ready.length} ${ready.length === 1 ? 'invite' : 'invites'}`}
              </button>
            )}
            {!sending && <button onClick={() => { setRows(null); setDone(false); }} className={secondaryButton}>{done ? 'Invite more' : 'Edit list'}</button>}
            {done && <button onClick={onClose} className={secondaryButton}>Done</button>}
          </div>
        </div>
      )}
    </Panel>
  );
}

function InviteResult({ result, onClose }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(result.invite_link); setCopied(true); } catch { /* clipboard blocked */ }
  };
  return (
    <div className={`${result.emailed ? noticeBox : 'bg-coral-soft border border-coral/40 text-ink px-4 py-3 rounded-lg text-sm'} space-y-2`}>
      <p className="font-semibold">
        {result.emailed
          ? `${result.resent ? 'Invitation sent again' : 'Invitation sent'} to ${result.email}.`
          : result.email_note}
        {result.emailed && result.email_note && <span className="font-normal"> {result.email_note}</span>}
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <code className="bg-white/70 px-2 py-1 rounded break-all text-xs">{result.invite_link}</code>
        <button onClick={copy} className="h-8 px-3 rounded-lg border border-line-strong bg-white text-xs font-semibold">{copied ? 'Copied' : 'Copy link'}</button>
        <button onClick={onClose} className="h-8 px-2 text-xs font-medium text-ink-muted hover:text-ink">Dismiss</button>
      </div>
    </div>
  );
}
